"use strict";
/*jshint esversion: 11 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { startLoadingSpinner, stopLoadingSpinner } from "../../csf-ufc-pivotal/includes/utils";
import CsfMembreDispensesDataLayer from "./CsfMembreDispensesDataLayer";
import PivotalAttachmentsApi
  from "../../../js/csf/pivotal/PivotalAttachmentsApi";
import PivotalApiEnvironments
  from "../../../js/csf/pivotal/PivotalApiEnvironments";
import {convertBase64} from "../../csf-ufc-fournisseur/includes/utils";

export default function CsfMembreDispenseListItemUploads({educationFileId, exemptionItem, dispenseDataService, apiUrl, apiEnv, accessToken}) {
  const [isError, setIsError] = useState(false);

  const pivotalAttachmentsApiRef = useRef();
  const dispenseDataServiceRef = useRef();
  const educationFileIdRef = useRef();
  const exemptionItemRef = useRef();


  // fields à gérer
  const ATTACHMENT_FIELDS = [
    "EM_Attachement",
    "EM_Attachement_2",
    "EM_Attachement_3",
    "EM_Attachement_4",
    "EM_Attachement_5"
  ];

  // liste des fichiers existants -- état initial : édition ou vide pour création
  const [attachments, setAttachments] = useState(() =>
    ATTACHMENT_FIELDS.reduce((acc, field) => ({
      ...acc,
      [field]: exemptionItem?.[field] || null,
    }), {})
  );
  const [newFiles, setNewFiles] = useState({});
  const newFilesRef = useRef({});
  const [deletedFiles, setDeletedFiles] = useState({});
  const deletedFilesRef = useRef({});

  // Premier ATTACHMENT_FIELDS dispo SANS pièce jointe
  const [firstEmptyField, setFirstEmptyField] = useState(null);


  // Ajout d’un fichier
  const handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      setAttachments(prev => ({ ...prev, [field]: file.name }));
      setNewFiles(prev => ({ ...prev, [field]: file }));
      newFilesRef.current = { ...newFilesRef.current, [field]: file }; // Ajout/ref synchro
      setDeletedFiles(prev => ({ ...prev, [field]: false }));
      deletedFilesRef.current = { ...deletedFilesRef.current, [field]: false };

      // save and reload page
      handleEditDispense();
    }
  };

  // Suppression d’une pièce jointe (existante ou neuve)
  const handleRemove = (field) => {
    setAttachments(prev => ({ ...prev, [field]: null }));
    setNewFiles(prev => ({ ...prev, [field]: null }));
    newFilesRef.current = { ...newFilesRef.current, [field]: null };
    setDeletedFiles(prev => ({ ...prev, [field]: true }));
    deletedFilesRef.current = { ...deletedFilesRef.current, [field]: true };

    // save and reload page
    handleEditDispense();
  };

  useEffect(() => {
    if (!pivotalAttachmentsApiRef.current) {
      pivotalAttachmentsApiRef.current = new PivotalAttachmentsApi(apiUrl, apiEnv, accessToken);
    }
  }, [apiUrl, apiEnv]);

  useEffect(() => {
    if (accessToken && pivotalAttachmentsApiRef.current) {
      pivotalAttachmentsApiRef.current.setAccessToken(accessToken);
    }
  }, [accessToken]);

  useEffect(() => {
    if (dispenseDataService) {
      dispenseDataServiceRef.current = dispenseDataService;
    }
  }, [dispenseDataService]);

  useEffect(() => {
    if (educationFileId) {
      educationFileIdRef.current = educationFileId;
    }
  }, [educationFileId]);

  useEffect(() => {
    setIsError(false);
    exemptionItemRef.current = exemptionItem;

    // réinitialiser les fichiers initiaux existants
    setAttachments(
      ATTACHMENT_FIELDS.reduce((acc, field) => ({
        ...acc,
        [field]: exemptionItem?.[field] || null,
      }), {})
    );

    // on réinitialise aussi newFiles, firstEmptyField, etc. ici
    setNewFiles({});
    newFilesRef.current = {};
    setDeletedFiles({});
    deletedFilesRef.current = {};

  }, [exemptionItem]);

  useEffect(() => {
    const emptyField = ATTACHMENT_FIELDS.find(
      field => !attachments[field] && !newFiles[field]
    );
    setFirstEmptyField(emptyField ?? null);
  }, [attachments, newFiles]);


  async function handleEditDispense() {
    startLoadingSpinner();

    // upload document if present
    const fileUploads = [];
    Object.entries(newFilesRef.current).forEach(([fieldName, file]) => {
      if (file) {
        // handle added or modified files
        const extension = file.name.split('.').pop();
        const nameWithoutExtension = file.name.slice(0, -(extension.length + 1));

        const lastModifiedDateTime = new Date(file.lastModifiedDate);
        const lastModifiedDate = `${lastModifiedDateTime.getMonth() + 1}/${lastModifiedDateTime.getDate()}/${lastModifiedDateTime.getFullYear()}`;
        const lastModifiedTime = `${lastModifiedDateTime.getHours()}:${lastModifiedDateTime.getMinutes()}:${lastModifiedDateTime.getSeconds()}`;

        fileUploads.push({
          file: file,
          fileName: file.name,
          nameWithoutExtension: nameWithoutExtension,
          extension: extension,
          size: file.size,
          fieldName: fieldName,
          lastModifiedDate: lastModifiedDate,
          lastModifiedTime: lastModifiedTime,
        });
      }
    });

    // Téléversement des fichiers en parallèle
    try {
      const uploadResults = await Promise.all(
        fileUploads.map(async fu => {
          // Conversion du fichier en base64 et appel de l’API de téléversement
          const fileBase64 = await convertBase64(fu.file);
          const requestData = {"uploadFileBase64": fileBase64};
          const response = await pivotalAttachmentsApiRef.current.callTaskUploadEndpoint(
            requestData,
            PivotalApiEnvironments.getEspaceMembreSuffix()
          );
          // Récupération de l’ID de l’upload retourné
          return {
            ...fu,
            uploadID: response.payload.uploadID,
          };
        })
      );


      // get values from form and submit to API
      let formData = {
        Education_File_Id: educationFileIdRef.current,
        Reason: exemptionItemRef.current.Reason,
        Start_Date: exemptionItemRef.current.Start_Date,
        End_Date: exemptionItemRef.current.End_Date,
      };

      // On ajoute dynamiquement pour chaque fichier les attributs nécessaires :
      uploadResults.forEach(ur => {
        formData[`${ur.fieldName}`] = ur.fileName;
        formData[`${ur.fieldName}__UploadID`] = ur.uploadID;
        formData[`RN_${ur.fieldName}_ED`] = ur.lastModifiedDate;
        formData[`RN_${ur.fieldName}_ET`] = ur.lastModifiedTime;
        formData[`RN_${ur.fieldName}_EX`] = ur.extension;
        formData[`RN_${ur.fieldName}_FN`] = ur.nameWithoutExtension;
        formData[`RN_${ur.fieldName}_FZ`] = ur.size;
      });

      // ajouter les fichiers à retirer
      Object.entries(deletedFilesRef.current).forEach(([fieldName, toDelete]) => {
        if (toDelete) {
          formData[`${fieldName}`] = false;
        }
      });

      // REDU_Exemption_Id provided as 1st parameter if saving existing dispense, else null to create
      dispenseDataServiceRef.current.submitDispenseData(
        exemptionItemRef.current?.REDU_Exemption_Id,
        formData,
        () => {
          stopLoadingSpinner();
          // on Success: reload page to refresh list
          window.location.reload();
        },
        () => {
          stopLoadingSpinner();
          setIsError(true);
        }
      );

    }
    catch (error) {
      setIsError(true);
      stopLoadingSpinner();
      console.error('Erreur lors du téléversement des fichiers :', error);
    }

  }


  // Rendu dynamique du champ d'attachement
  function renderAttachmentField(field) {
    const filename = attachments[field];
    const isNew = Boolean(newFiles[field]);

    if (filename) {
      return (
        <div className="form-group file-row" key={field}>
          <div className="file-custom">
            <div className="selectedFile">
              <span className="visually-hidden">{window.Drupal.t('fournisseur.gestion.file.selected')} : </span>
              {isNew ? (
              <span className="name">
                {filename}
              </span>
              ) : (
                <a href="#"
                  className="name"
                  onClick={() =>
                    pivotalAttachmentsApiRef.current.downloadAttachment({
                        form: 'CSF_EM_Exemption_REST',
                        recordId: exemptionItem.REDU_Exemption_Id,
                        fieldName: field,
                        filename: filename,
                        action: PivotalAttachmentsApi.getNewTabAction()
                      },
                      PivotalApiEnvironments.getEspaceMembreSuffix()
                    )
                  }
                >
                  {filename}
                </a>
              )}
              <button
                className="removeFile"
                data-toggle="tooltip"
                title={`${window.Drupal.t('fournisseur.gestion.file.deselected')} ${filename}`}
                onClick={e => { e.preventDefault(); handleRemove(field); }}
              >
                <i className="icon icon-remove"></i>
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Montre input file **seulement pour le premier champ à remplir**
    if (firstEmptyField === field) {
      return (
        <div key={field} className="form-group file-row">
          <div className="file-custom">
            <div className="toSelectFile">
              <input type="hidden" className="file-name" name="" />
              <input
                type="file"
                className="visually-hidden"
                id={`dispense-document-${field}`}
                name={`dispense-document-${field}`}
                accept=".doc,.docx,.pdf,.png,.jpg,.jpeg"
                onChange={e => handleFileChange(field, e)}
              />
              <span className="name">
                {window.Drupal.t('membre.dispense.type.input-file.label')}
              </span>

              <label htmlFor={`dispense-document-${field}`}>
                {window.Drupal.t('fournisseur.gestion.file.btn')}
              </label>
            </div>
          </div>
        </div>
      );
    }
    // Rien sinon
    return null;
  }


  return (
    <div>
        {isError === true && (
          <div className="alert alert-danger" role="alert"
               aria-live="polite">{window.Drupal.t('broker.manager.error.message')}</div>
        )}
        {ATTACHMENT_FIELDS.map(renderAttachmentField)}
    </div>
  )
}

CsfMembreDispenseListItemUploads.propTypes = {
  educationFileId: PropTypes.string,
  exemptionItem: PropTypes.object,
  dispenseDataService: PropTypes.oneOfType([
    PropTypes.instanceOf(CsfMembreDispensesDataLayer),
    PropTypes.object, // éviter warning du mauvais type avant son initialisation
  ]),
  apiUrl: PropTypes.string,
  apiEnv: PropTypes.string,
  accessToken: PropTypes.string,
};

